/**
 * @file components/shared/DailyQuoteModal.jsx
 * @description Refined motivational overlay with consistent branding.
 */

import { X } from "lucide-react";
import { useEffect, useState } from "react";
import { getTodaysQuote } from "../../constants/motivationalQuotes";

const QUOTE_COLORS = {
  fitness: {
    gradient: "from-brand-green to-emerald-500",
    glow: "shadow-brand-green/20",
    icon: "🏋️",
  },
  health: {
    gradient: "from-blue-400 to-cyan-500",
    glow: "shadow-blue-400/20",
    icon: "💚",
  },
  nutrition: {
    gradient: "from-amber-400 to-orange-500",
    glow: "shadow-amber-400/20",
    icon: "🥗",
  },
  // REPLACED PINK: Using a sleek Cyan/Slate for a "Chrome" motivation look
  motivation: {
    gradient: "from-cyan-400 to-slate-400",
    glow: "shadow-cyan-400/20",
    icon: "✨",
  },
};

export function DailyQuoteModal() {
  const [isOpen, setIsOpen] = useState(false);
  const [quote, setQuote] = useState(null);

  useEffect(() => {
    const today = new Date().toDateString();
    const lastSeen = localStorage.getItem("lastQuoteSeen");

    if (lastSeen !== today) {
      const todaysQuote = getTodaysQuote();
      setQuote(todaysQuote);
      setIsOpen(true);
    }
  }, []);

  const handleDismiss = () => {
    localStorage.setItem("lastQuoteSeen", new Date().toDateString());
    setIsOpen(false);
  };

  if (!isOpen || !quote) return null;

  const colors = QUOTE_COLORS[quote.category] || QUOTE_COLORS.motivation;

  return (
    <div className="fixed inset-0 z-[300] flex items-center justify-center p-4">
      {/* Backdrop with heavy blur for focus */}
      <div
        className="absolute inset-0 bg-[#0B0B0B]/80 backdrop-blur-xl animate-in fade-in duration-500"
        onClick={handleDismiss}
      />

      {/* Modal Container */}
      <div
        className={`
        relative w-full max-w-[280px] bg-[#121417] border border-white/5 
        rounded-[2.5rem] p-8 text-center shadow-2xl ${colors.glow}
        animate-in zoom-in-95 slide-in-from-bottom-4 duration-500
      `}
      >
        {/* Subtle Gradient Glow behind content */}
        <div
          className={`absolute inset-0 bg-gradient-to-b ${colors.gradient} opacity-[0.03] rounded-[2.5rem]`}
        />

        <button
          onClick={handleDismiss}
          className="absolute top-6 right-6 text-gray-500 hover:text-white transition-colors"
        >
          <X size={18} />
        </button>

        {/* Header Section */}
        <div className="relative z-10 mb-6">
          <div className="text-4xl mb-4 drop-shadow-lg">{colors.icon}</div>
          <p className="text-[10px] uppercase tracking-[0.3em] font-black text-gray-500">
            Daily Momentum
          </p>
        </div>

        {/* Quote Content */}
        <div className="relative z-10 mb-8">
          <p className="text-lg font-serif italic text-white leading-tight mb-4">
            &quot;{quote.quote}&quot;
          </p>

          <div className="flex items-center justify-center gap-2">
            <div
              className={`h-px w-4 bg-gradient-to-r ${colors.gradient} opacity-50`}
            />
            <span className="text-[9px] uppercase tracking-widest font-bold text-gray-400">
              {quote.category}
            </span>
            <div
              className={`h-px w-4 bg-gradient-to-l ${colors.gradient} opacity-50`}
            />
          </div>
        </div>

        {/* CTA Section */}
        <div className="relative z-10 space-y-4">
          <button
            onClick={handleDismiss}
            className={`
              w-full py-4 px-6 rounded-2xl font-bold text-[11px] uppercase tracking-[0.15em]
              bg-gradient-to-r ${colors.gradient} text-[#0B0B0B]
              shadow-lg active:scale-95 transition-all duration-200
            `}
          >
            Start Tracking
          </button>

          <p className="text-[9px] text-gray-600 font-medium tracking-wide italic">
            &quot;One day at a time. Build consistency.&quot;
          </p>
        </div>
      </div>
    </div>
  );
}
